import React, { useState, useEffect, useContext } from 'react';
import { 
    Card, 
    CardContent, 
    CardActions, 
    Typography, 
    Checkbox, 
    IconButton, 
    Menu, 
    MenuItem, 
    Box, 
    Grid
} from '@mui/material';
import { MoreVert, Edit, Delete } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { AlertContext } from "../App/App";
import { colorLink, colorText } from "../App/theme";
import { formatScore } from "../utils";
import { decryptKBItemIfEncrypted, getItemImageUrl } from "../App/cryptoUtils";
import { deleteItem } from "../API/items/itemsAPI";
import { useConfirm } from "../Components/ConfirmContext";
import { JSONComponent } from "../ContentWidgets/JSONComponent";

const ItemsCards = React.memo(({
    embeddingConfigured, items, selectMode, selectedItems,
    devMode, kbId, itemsIndexedDB, itemsVectorDB, setSelectedItems, KBData, setItems
}) => {
    const [renderedItemsCount, setRenderedItemsCount] = useState(20);
    const { systemAlert, setSystemAlert } = useContext(AlertContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    const showConfirm = useConfirm();
    
    const handleMenuClick = (event, itemId) => {
        setAnchorEl(event.currentTarget);
        setSelectedItem(itemId);
    };

    const onDelete = async (kbId, itemId, itemType) => {
        try {
            await deleteItem({ itemId, KBData, itemType });
            if (items) setItems(items.filter((item) => item.meta.itemId !== itemId));
        } catch (error) {
            if (error?.response?.data?.error) alert(error.response.data.error);
            console.error('Error deleting item:', error);
        }
    };

    const handleDelete = async (kbId, itemId, itemType) => {
        const confirmed = await showConfirm({
            title: 'Delete Item Confirmation',
            content: `Are you sure you want to delete item ${itemId}?`
        });

        if (confirmed) {
            onDelete(kbId, itemId, itemType);
        }
    };

    const handleSelect = (itemId) => {
        if (selectedItems.includes(itemId)) {
            setSelectedItems(selectedItems.filter(id => id !== itemId));
        } else {
            setSelectedItems([...selectedItems, itemId]);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleScroll = () => {
        const bottom = Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight;
        if (bottom) {
            const length = itemsVectorDB?.length || itemsIndexedDB?.length || items?.length;
            setRenderedItemsCount(prevCount => {
                return Math.min(prevCount + 20, length) || 20;
            });
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [items, itemsIndexedDB, itemsVectorDB]);


    const myItems = itemsVectorDB || itemsIndexedDB || items;

    if (!embeddingConfigured || !myItems) return null;

    return (
        <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
                {myItems.slice(0, renderedItemsCount).map(({ item, meta }) => {
                    const imageItem = getItemImageUrl({ item, meta });
                    
                    // Get all non-itemId attributes
                    const attributes = meta?.attributes?.filter(attr => attr.attrType !== 'itemId') || [];

                    return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={meta.itemId}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    '&:hover': {
                                        boxShadow: 3,
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.2s ease-in-out'
                                    }
                                }}
                            >
                                {/* Selection Checkbox */}
                                {selectMode && (
                                    <Checkbox
                                        sx={{ 
                                            position: 'absolute',
                                            top: 8,
                                            left: 8,
                                            zIndex: 1,
                                            backgroundColor: 'white',
                                            borderRadius: '4px'
                                        }}
                                        checked={selectedItems.includes(meta.itemId)}
                                        onChange={() => handleSelect(meta.itemId)}
                                    />
                                )}

                                {/* Actions Menu */}
                                {!itemsIndexedDB && !itemsVectorDB && (
                                    <IconButton
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            '&:hover': { backgroundColor: 'white' }
                                        }}
                                        onClick={(e) => handleMenuClick(e, meta.itemId)}
                                    >
                                        <MoreVert />
                                    </IconButton>
                                )}

                                <CardContent sx={{ flexGrow: 1, pt: selectMode ? 6 : 3 }}>
                                    {/* Item ID and Type */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography
                                            variant="h6"
                                            component={Link}
                                            to={`/edit-item/${meta.itemId}?returnTo=database`}
                                            sx={{ 
                                                color: colorLink,
                                                textDecoration: 'none',
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                '&:hover': { textDecoration: 'underline' },
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {meta.itemId}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Typography variant="body2" sx={{ 
                                                color: 'text.secondary',
                                                fontSize: '0.8rem',
                                                fontWeight: 500
                                            }}>
                                                {meta.itemType}
                                            </Typography>
                                            {meta.score && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    Score: {meta.score === 1 ? 100 : formatScore(meta.score)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Attributes - Compact Grid Layout */}
                                    <Box sx={{ mb: 2 }}>
                                        {(() => {
                                            // Group attributes by type for better layout
                                            const textAttrs = [];
                                            const numberAttrs = [];
                                            const dateAttrs = [];
                                            const longTextAttrs = [];
                                            const jsonAttrs = [];
                                            
                                            attributes.forEach(attr => {
                                                const value = attr.encrypted 
                                                    ? decryptKBItemIfEncrypted(item[attr.attrName])
                                                    : (item[attr.attrName] === true && 'True' || item[attr.attrName]) || '';
                                                
                                                if (!value) return;
                                                
                                                const attrData = { ...attr, value };
                                                
                                                // Check if it's a body field with JSON content
                                                if (attr.attrType === 'body' || attr.attrName === 'body') {
                                                    // Try to parse as JSON
                                                    try {
                                                        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
                                                        jsonAttrs.push({ ...attrData, parsedValue: parsed });
                                                        return;
                                                    } catch {
                                                        // Not JSON, treat as regular text
                                                        longTextAttrs.push(attrData);
                                                        return;
                                                    }
                                                }
                                                
                                                // Detect dates (timestamps or date strings)
                                                if (attr.attrName.toLowerCase().includes('date') || 
                                                    attr.attrName.toLowerCase().includes('time') ||
                                                    attr.attrName.toLowerCase().includes('created') ||
                                                    attr.attrName.toLowerCase().includes('updated') ||
                                                    (typeof value === 'string' && /^\d{13}$/.test(value))) {
                                                    dateAttrs.push(attrData);
                                                }
                                                // Detect numbers (but exclude timestamps)
                                                else if (!isNaN(value) && !isNaN(parseFloat(value)) && 
                                                         !attr.attrName.toLowerCase().includes('created') &&
                                                         !attr.attrName.toLowerCase().includes('updated') &&
                                                         !/^\d{13}$/.test(value.toString())) {
                                                    numberAttrs.push(attrData);
                                                }
                                                // Long text (addresses, descriptions)
                                                else if (typeof value === 'string' && value.length > 50) {
                                                    longTextAttrs.push(attrData);
                                                }
                                                // Short text
                                                else {
                                                    textAttrs.push(attrData);
                                                }
                                            });
                                            
                                            return (
                                                <>
                                                    {/* Short text fields - labels above */}
                                                    {textAttrs.length > 0 && (
                                                        <Box sx={{ 
                                                            display: 'grid', 
                                                            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                                                            gap: 0.5,
                                                            mb: 1
                                                        }}>
                                                            {textAttrs.map(attr => (
                                                                <Box key={attr.attrName}>
                                                                    <Typography variant="caption" sx={{ 
                                                                        color: 'text.secondary', 
                                                                        fontSize: '0.45rem',
                                                                        fontWeight: 500,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.2px',
                                                                        lineHeight: 1,
                                                                        display: 'block',
                                                                        mb: 0.25,
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        maxWidth: '100%'
                                                                    }}>
                                                                        {attr.attrName.replace(/([A-Z])/g, ' $1').trim()}
                                                                    </Typography>
                                                                    <Box sx={{ 
                                                                        backgroundColor: 'rgba(0,0,0,0.02)',
                                                                        border: '1px solid rgba(0,0,0,0.1)',
                                                                        borderRadius: 1,
                                                                        px: 0.75,
                                                                        py: 0.5,
                                                                        minHeight: '24px',
                                                                        display: 'flex',
                                                                        alignItems: 'center'
                                                                    }}>
                                                                        <Typography variant="body2" sx={{ 
                                                                            fontWeight: 500,
                                                                            fontSize: '0.8rem',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            lineHeight: 1.1,
                                                                            width: '100%'
                                                                        }}>
                                                                            {attr.value}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                    
                                                    {/* Number fields - labels above */}
                                                    {numberAttrs.length > 0 && (
                                                        <Box sx={{ 
                                                            display: 'grid', 
                                                            gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
                                                            gap: 0.5,
                                                            mb: 1
                                                        }}>
                                                            {numberAttrs.map(attr => (
                                                                <Box key={attr.attrName}>
                                                                    <Typography variant="caption" sx={{ 
                                                                        color: 'primary.main', 
                                                                        fontSize: '0.4rem',
                                                                        fontWeight: 600,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.2px',
                                                                        lineHeight: 1,
                                                                        display: 'block',
                                                                        mb: 0.25,
                                                                        textAlign: 'center',
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        maxWidth: '100%'
                                                                    }}>
                                                                        {attr.attrName.replace(/([A-Z])/g, ' $1').trim()}
                                                                    </Typography>
                                                                    <Box sx={{ 
                                                                        backgroundColor: 'rgba(25,118,210,0.05)',
                                                                        borderRadius: 1,
                                                                        px: 0.5,
                                                                        py: 0.5,
                                                                        textAlign: 'center',
                                                                        border: '1px solid rgba(25,118,210,0.2)',
                                                                        minHeight: '24px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center'
                                                                    }}>
                                                                        <Typography variant="body2" sx={{ 
                                                                            fontWeight: 600,
                                                                            fontSize: '0.75rem',
                                                                            color: 'primary.dark',
                                                                            fontFamily: 'monospace',
                                                                            lineHeight: 1
                                                                        }}>
                                                                            {parseFloat(attr.value).toLocaleString()}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                    
                                                    {/* Date fields - horizontal layout */}
                                                    {dateAttrs.length > 0 && (
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            flexWrap: 'wrap',
                                                            gap: 1,
                                                            mb: 1.5
                                                        }}>
                                                            {dateAttrs.map(attr => {
                                                                const dateValue = /^\d{13}$/.test(attr.value) 
                                                                    ? new Date(parseInt(attr.value)).toLocaleDateString()
                                                                    : attr.value;
                                                                    
                                                                return (
                                                                    <Box key={attr.attrName} sx={{ 
                                                                        backgroundColor: 'rgba(76,175,80,0.05)',
                                                                        borderRadius: 1,
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.5,
                                                                        border: '1px solid rgba(76,175,80,0.2)'
                                                                    }}>
                                                                        <Typography variant="caption" sx={{ 
                                                                            color: 'success.main', 
                                                                            fontSize: '0.45rem',
                                                                            fontWeight: 500,
                                                                            textTransform: 'uppercase',
                                                                            letterSpacing: '0.2px'
                                                                        }}>
                                                                            {attr.attrName.replace(/([A-Z])/g, ' $1').trim()}:
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ 
                                                                            fontWeight: 500,
                                                                            fontSize: '0.7rem'
                                                                        }}>
                                                                            {dateValue}
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                    
                                                    {/* Long text fields - full width */}
                                                    {longTextAttrs.map(attr => (
                                                        <Box key={attr.attrName} sx={{ mb: 1 }}>
                                                            <Typography variant="caption" sx={{ 
                                                                color: 'text.secondary', 
                                                                fontSize: '0.55rem',
                                                                fontWeight: 500,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.3px'
                                                            }}>
                                                                {attr.attrName.replace(/([A-Z])/g, ' $1').trim()}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ 
                                                                mt: 0.25,
                                                                fontSize: '0.8rem',
                                                                lineHeight: 1.3,
                                                                backgroundColor: 'rgba(0,0,0,0.02)',
                                                                p: 1,
                                                                borderRadius: 1,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical'
                                                            }}>
                                                                {attr.value}
                                                            </Typography>
                                                            
                                                            {/* Display image if this attribute contains an image URL */}
                                                            {imageItem && attr.attrName === imageItem.imageUrlAttributeName && imageItem.imageUrl && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <img 
                                                                        src={imageItem.imageUrl} 
                                                                        alt={`${attr.attrName}`}
                                                                        style={{ 
                                                                            width: '100%', 
                                                                            maxHeight: '120px', 
                                                                            objectFit: 'cover',
                                                                            borderRadius: '4px'
                                                                        }}
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ))}
                                                    
                                                    {/* JSON fields - with interactive viewer */}
                                                    {jsonAttrs.map(attr => (
                                                        <Box key={attr.attrName} sx={{ mb: 1.5 }}>
                                                            <Typography variant="caption" sx={{ 
                                                                color: 'info.main', 
                                                                fontSize: '0.55rem',
                                                                fontWeight: 600,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.3px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                mb: 0.5
                                                            }}>
                                                                {attr.attrName.replace(/([A-Z])/g, ' $1').trim()}
                                                                <span style={{ 
                                                                    fontSize: '0.45rem', 
                                                                    backgroundColor: 'rgba(25,118,210,0.1)',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px',
                                                                    fontWeight: 500
                                                                }}>JSON</span>
                                                            </Typography>
                                                            <Box sx={{ 
                                                                backgroundColor: 'rgba(0,0,0,0.03)',
                                                                borderRadius: 1,
                                                                border: '1px solid rgba(25,118,210,0.15)',
                                                                overflow: 'hidden',
                                                                maxHeight: '200px',
                                                                overflowY: 'auto',
                                                                '&::-webkit-scrollbar': {
                                                                    width: '6px',
                                                                },
                                                                '&::-webkit-scrollbar-track': {
                                                                    backgroundColor: 'rgba(0,0,0,0.05)',
                                                                },
                                                                '&::-webkit-scrollbar-thumb': {
                                                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                                                    borderRadius: '3px',
                                                                }
                                                            }}>
                                                                <Box sx={{ 
                                                                    display: 'flex', 
                                                                    justifyContent: 'flex-start',
                                                                    width: '100%'
                                                                }}>
                                                                    <JSONComponent 
                                                                        data={attr.parsedValue} 
                                                                        collapsed={1}
                                                                        enableClipboard={false}
                                                                        props={{
                                                                            style: {
                                                                                fontSize: 11,
                                                                                padding: '8px',
                                                                                backgroundColor: 'transparent',
                                                                                textAlign: 'left'
                                                                            },
                                                                            displayObjectSize: false,
                                                                            quotesOnKeys: false,
                                                                            indentWidth: 2,
                                                                            collapseStringsAfterLength: 50
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </>
                                            );
                                        })()}
                                    </Box>

                                    {/* Timestamps - Compact inline */}
                                    <Box sx={{ 
                                        mt: 'auto',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        borderTop: '1px solid rgba(0,0,0,0.06)',
                                        pt: 1
                                    }}>
                                        <Typography variant="caption" sx={{ 
                                            color: 'text.secondary',
                                            fontSize: '0.6rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            <span style={{ fontWeight: 500 }}>Created:</span>
                                            {new Date(parseInt(meta.createdAt)).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="caption" sx={{ 
                                            color: 'text.secondary',
                                            fontSize: '0.6rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            <span style={{ fontWeight: 500 }}>Updated:</span>
                                            {new Date(parseInt(meta.updatedAt)).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </CardContent>

                                {/* Actions Menu */}
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl) && selectedItem === meta.itemId}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem
                                        component={Link}
                                        to={`/edit-item/${meta.itemId}?returnTo=database`}
                                        onClick={handleMenuClose}
                                    >
                                        <Edit fontSize="small" sx={{ mr: '12px' }} />
                                        Edit
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            handleDelete(kbId, meta.itemId, meta.itemType);
                                            handleMenuClose();
                                        }}
                                    >
                                        <Delete fontSize="small" sx={{ mr: '12px' }} />
                                        Delete
                                    </MenuItem>
                                </Menu>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
});

export default ItemsCards;